import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { quizApi, examLibraryApi, batchApi, apiErrorMessage } from '../services/api';
import { useOrg } from '../context/OrgContext';
import { useToast } from '../context/ToastContext';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import { Button, Card, PageSkeleton, ErrorState, EmptyState } from '../components/ui';
import LaunchDialog from '../components/dialogs/LaunchDialog';
import { resolveQuizState, quizStateLabel, QUIZ_STATE } from '../utils/quizState';
import { quizActions } from '../utils/quizActions';
import QuizActionDialog from '../components/dialogs/QuizActionDialog';

const MODE_LABELS = { quick_launch: 'Quick launch', screener: 'Screener', scheduled: 'Scheduled' };
// Keyed by the computed state, not the stored `status`: that is written once at
// creation and never advances, so it cannot say whether a test is live.
const STATE_CLASS = {
  [QUIZ_STATE.OPEN]: 'status',
  [QUIZ_STATE.UPCOMING]: 'pending',
  [QUIZ_STATE.CLOSED]: 'status draft',
};

export default function ManageQuizPage() {
  const org = useOrg();
  const toast = useToast();
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState([]);
  const [exams, setExams] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  // { action: 'close' | 'edit' | 'delete', quiz } while its dialog is open.
  const [pending, setPending] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([quizApi.list(org.id), examLibraryApi.enrolled(org.id), batchApi.list(org.id)])
      .then(([quizzesRes, examsRes, batchesRes]) => {
        setQuizzes(quizzesRes.data || []);
        setExams(examsRes.data || []);
        setBatches(batchesRes.data || []);
        setError(null);
      })
      .catch((err) => setError(apiErrorMessage(err, 'Failed to load quizzes')))
      .finally(() => setLoading(false));
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const openQuickLaunch = () => {
    if (!exams.length) return toast.error('Enroll in an exam from the Exam library first.');
    if (!batches.length) return toast.error('Create a batch first so the quiz has an audience.');
    setDialogOpen(true);
  };

  if (loading) return <PageSkeleton />;
  if (error) return <ErrorState message={error} />;

  const batchNames = Object.fromEntries(batches.map((b) => [b.id, b.name]));
  // Row buttons sit inside a clickable row that opens the results page.
  const offer = (action, quiz) => (e) => { e.stopPropagation(); setPending({ action, quiz }); };

  return (
    <>
      <div className="section-title">
        <div>
          <p className="eyebrow">TWO WAYS TO LAUNCH</p>
          <h2>Choose the right quiz workflow</h2>
        </div>
        <Button onClick={openQuickLaunch}>+ Quick launch</Button>
      </div>
      <div className="card-grid launch-options">
        <Card className="hoverable">
          <span className="step-icon"><BoltOutlinedIcon fontSize="small" /></span>
          <h2>Quick launch</h2>
          <p>Randomly select questions from an enrolled exam and send it to a batch in a few clicks.</p>
          <footer><Button variant="secondary" onClick={openQuickLaunch}>Quick launch</Button></footer>
        </Card>
        <Card className="hoverable">
          <span className="step-icon"><TuneOutlinedIcon fontSize="small" /></span>
          <h2>Launch with screener</h2>
          <p>Design the quiz first: set topics, difficulty and question count, then schedule it for students.</p>
          <footer><Button onClick={() => navigate('/screener')}>Open screener</Button></footer>
        </Card>
        <Card className="hoverable">
          <span className="step-icon"><EventOutlinedIcon fontSize="small" /></span>
          <h2>Schedule for a batch</h2>
          <p>Both routes let you pick a batch, opening time and attempt limit before launching.</p>
          <footer><Button variant="secondary" onClick={openQuickLaunch}>Schedule quiz</Button></footer>
        </Card>
      </div>
      <Card className="table-card">
        <div className="section-title" style={{ padding: '17px 16px 0' }}>
          <div>
            <h2>Your tests</h2>
            <p>{exams.length ? `${exams.length} enrolled exams ready to launch from` : 'Enroll in an exam to start launching quizzes.'}</p>
          </div>
        </div>
        <table>
          <thead><tr><th>Quiz</th><th>Mode</th><th>Batch</th><th>Questions</th><th>Time limit</th><th>Opens</th><th>Closes</th><th>State</th><th></th></tr></thead>
          <tbody>
            {quizzes.length ? quizzes.map((q) => {
              const state = resolveQuizState(q);
              // Attempts are not counted here, so delete shows only for tests
              // that have not opened; the results page offers it otherwise.
              const actions = quizActions(q);
              return (
                <tr key={q.id} className="clickable" onClick={() => navigate(`/manage-quiz/${q.id}`)}>
                  <td><b>{q.name}</b></td>
                  <td>{MODE_LABELS[q.mode] || q.mode}</td>
                  <td>{batchNames[q.batchId] || q.batchId || '—'}</td>
                  <td>{q.questionCount}</td>
                  <td>{q.durationMinutes ? `${q.durationMinutes} min` : '—'}</td>
                  <td>{q.opensAt ? new Date(q.opensAt).toLocaleString() : 'Immediately'}</td>
                  <td>{q.closesAt ? new Date(q.closesAt).toLocaleString() : 'No deadline'}</td>
                  <td><span className={STATE_CLASS[state] || 'status'}>{quizStateLabel(state)}</span></td>
                  <td>
                    <div className="row-actions">
                      <Button variant="text">Results</Button>
                      {actions.close.allowed && <Button variant="text" onClick={offer('close', q)}>Close now</Button>}
                      <Button variant="text" onClick={offer('edit', q)}>Edit</Button>
                      {actions.remove.allowed && <Button variant="text" onClick={offer('delete', q)}>Delete</Button>}
                    </div>
                  </td>
                </tr>
              );
            }) : (
              <tr><td colSpan="9">
                <EmptyState title="No quizzes scheduled yet" action={<Button variant="secondary" onClick={openQuickLaunch}>Quick launch a quiz</Button>}>
                  Launch your first quiz to see it tracked here.
                </EmptyState>
              </td></tr>
            )}
          </tbody>
        </table>
      </Card>
      {dialogOpen && (
        <LaunchDialog
          exams={exams}
          batches={batches}
          onClose={() => setDialogOpen(false)}
          onLaunched={() => { setDialogOpen(false); load(); }}
        />
      )}
      {pending && (
        <QuizActionDialog
          action={pending.action}
          quiz={pending.quiz}
          onCancel={() => setPending(null)}
          onDone={() => { setPending(null); load(); }}
        />
      )}
    </>
  );
}
